/*
 * Position dots for the testimonial slider.
 *
 * Deliberately does not reuse the theme's .slider-counter__link handling in
 * global.js: that code derives the current page from `slider.scrollLeft`, which
 * is negative in an RTL document, so it counts 1, 0, -1 instead of 1, 2, 3.
 * Measuring rectangles instead keeps this correct in both directions.
 */
class DsSliderDots extends HTMLElement {
  connectedCallback() {
    this.slider = document.getElementById(this.dataset.slider);
    this.dots = Array.from(this.querySelectorAll('button'));
    if (!this.slider || this.dots.length < 2) return;

    this.onScroll = this.onScroll.bind(this);
    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
    this.sync();
  }

  disconnectedCallback() {
    if (!this.slider) return;
    this.slider.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  get items() {
    return Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter((el) => el.clientWidth > 0);
  }

  get isRtl() {
    return getComputedStyle(this.slider).direction === 'rtl';
  }

  /* Distance between a slide and the reading-start edge of the track. */
  offsetFromStart(item, sliderRect) {
    const rect = item.getBoundingClientRect();
    return this.isRtl ? rect.right - sliderRect.right : rect.left - sliderRect.left;
  }

  goTo(index) {
    const item = this.items[index];
    if (!item) return;
    this.slider.scrollBy({
      left: this.offsetFromStart(item, this.slider.getBoundingClientRect()),
      behavior: 'smooth',
    });
  }

  /* Reading three rects per scroll event is cheap enough that queueing the work
     on rAF is not worth the frozen-dots failure mode if a frame never lands. */
  onScroll() {
    this.sync();
  }

  sync() {
    const items = this.items;
    if (!items.length) return;

    const sliderRect = this.slider.getBoundingClientRect();
    let active = 0;
    let smallest = Infinity;

    items.forEach((item, index) => {
      const distance = Math.abs(this.offsetFromStart(item, sliderRect));
      if (distance < smallest) {
        smallest = distance;
        active = index;
      }
    });

    this.dots.forEach((dot, index) => {
      const isActive = index === active;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('ds-slider-dots')) {
  customElements.define('ds-slider-dots', DsSliderDots);
}
